import {IDeadFeature, IMentalGameData} from "../../server/MentalData";
import {Mental} from "../../Mental";
import {MentalNormalSymbol} from "../../reel/symbols/MentalNormalSymbol";
import {TimelineLite} from "gsap";
import {ReelModelState, ReelModelSymbolData} from "../../../framework/reel/ReelModel";
import {MentalSymbolStates} from "../../reel/MentalSymbol";
import {MentalGameMode} from "../../server/MentalGameMode";
import {DeadTransform} from "./DeadTransform";
import {DeadToActive, IDeadToActiveParams} from "./DeadToActive";
import {FeaturePresentationHelper} from "../../utils/FeaturePresentationHelper";
import {MentalReel} from "../../reel/MentalReel";
import {WinRatioType} from "../../../framework/winPresentation/IWinRatios";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {MentalSoundConfig} from "../../MentalSoundConfig";
import {SoundManager} from "../../sound/SoundManager";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export class MentalDeadFeature extends PIXI.Container {
    public _gameData: IMentalGameData;
    public _activeIndex: boolean[] = [false, false, false, false, false];
    private _deadSymCount: number = 0;
    private _deadToActiveItems: DeadToActive[][] = [];
    private _deadToActiveUsedCount: number[] = [0, 0, 0, 0, 0]; // this count is reel wise.

    constructor() {
        super();

        this.position.set(70, 90);
    }

    public initDeadToActive(): void {
        const layer: StageLayer = NolimitApplication.getLayerByName(Mental.LAYERS.DEAD_TO_ACTIVE)!,
            numReels: number = 5,
            deadItemsPossibleInAReel: number = 4;

        layer.position.set(70, 90);

        for (let reel: number = 0; reel < numReels; reel++) {
            this._deadToActiveItems[reel] = [];
            for (let i: number = 0; i < deadItemsPossibleInAReel; i++) {
                this._deadToActiveItems[reel].push(layer.addChild(new DeadToActive()));
            }
        }
    }

    /**
     * function to check if dead feature is triggered orr not
     */
    public checkForPresentation(): boolean {
        this._gameData = Mental.model.gameData;
        return this._gameData.isDeadFeatureTriggered;
    }

    /**
     * function to play dead feature presentation
     * replacing dead symbol with another Mid symbol getting from symbol
     * adding multiplier on it.
     */
    public playPresentation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite({paused: true}),
            otherSymbols: boolean[][][] = [];
        for (let i: number = 0; i < this._gameData.deadSymbols.length; i++) {
            otherSymbols[i] = [];

            for (let j: number = 0; j < this._gameData.deadSymbols[i].length; j++) {
                otherSymbols[i][j] = ArrayHelper.initArrayWithValues(this._gameData.currentSymbolSize[i][j], () => false);

                if (this._gameData.deadSymbols[i][j].length) {
                    const symPos: number = j,
                        symArr: string[] = [],
                        multiArr: number[] = [],
                        deadSymData: IDeadFeature[] = this._gameData.deadSymbols[i][symPos];

                    let absolutePosition: number = 0,
                        segmentPos: number = 0;
                    if (i === 1 && this._gameData.scatter2Position[i] === j) {
                        const featureTriggered: string[] = this._gameData.firstS2FeatureTriggered
                        const featureSize: number[] = this._gameData.firstS2FeatureSize;
                        for (let l: number = 0; l < featureTriggered.length; l++) {
                            if (featureTriggered[l] === "D") {
                                segmentPos += l;
                                break;
                            } else {
                                absolutePosition += featureSize[l];
                            }
                        }
                    } else if (i === 3 && this._gameData.scatter2Position[i] === j) {
                        const featureTriggered: string[] = this._gameData.secondS2FeatureTriggered;
                        const featureSize: number[] = this._gameData.secondS2FeatureSize;
                        for (let l: number = 0; l < featureTriggered.length; l++) {
                            if (featureTriggered[l] === "D") {
                                segmentPos += l;
                                break;
                            } else {
                                absolutePosition += featureSize[l];
                            }
                        }
                    }

                    for (let k: number = 0; k < deadSymData.length; k++) {
                        symArr.push(deadSymData[k].symbol);
                        multiArr.push(deadSymData[k].multiplier);

                        let deadSymTransform: DeadTransform = new DeadTransform({
                            reelId: i,
                            symId: symPos,
                            size: this._gameData.currentSymbolSize[i][j],
                            segment: segmentPos + k, // k always be 0  for reelId===3 and for only S2
                            revealTime: true,
                            absoluteIdx: absolutePosition + k
                        });
                        tl.add(() => {
                            deadSymTransform.name = "deadTransform_" + i + "_" + symPos + "_" + k;
                            this.addChild(deadSymTransform);

                            if (deadSymData[k].multiplier === Mental.specialDeadMultiplierReveal) {
                                otherSymbols[i][j][k] = true;
                            }
                        }, 0.05);
                        tl.add(deadSymTransform.playAnimSeq(absolutePosition + k, k), 0);
                    }

                    const sumOfMultArr: number = multiArr.reduce((a: number, b: number) => a + b, 0);

                    let symDiff: number = 0;

                    if (!(this.isOnEnhancer(i, j))) {
                        symDiff = multiArr.length;
                    }

                    Mental.xWaysCounter.updateXwaysCounterFromDead(i, sumOfMultArr, symDiff);

                    const symbolData: ReelModelSymbolData = Mental.reelArea.getReel(i).model.getReelSymbolAt(symPos),
                        symbol: MentalNormalSymbol = <MentalNormalSymbol>symbolData.positionable,
                        size: number = symbol._splitSymbols.length ? symbol._splitSymbols.length : 1;
                    let symName: string[] = symArr,
                        multiplierArr: number[] = multiArr,
                        newSym: MentalNormalSymbol;

                    if (this._gameData.scatter2Position[i] === symPos) {
                        symName = [];
                        multiplierArr = [];
                        const featureSymName: string[] = i === 1 ? this._gameData.firstS2SymbolName : this._gameData.secondS2SymbolName;

                        for (let k: number = 0; k < featureSymName.length; k++) {
                            if (featureSymName[k].indexOf("D") > -1) {
                                symName.push(symArr.shift() as string);
                                multiplierArr.push(multiArr.shift() as number);
                            } else {
                                symName.push(featureSymName[k]);
                                multiplierArr.push(1);
                            }
                        }
                    }

                    tl.add(() => {
                        newSym = <MentalNormalSymbol>Mental.reelArea.getReel(i).model.updateReelSymbolAt(symPos, symName[0]).positionable;
                        newSym._symbolLayer.position = symbol._symbolLayer.position;
                    }, 0);

                    tl.add(() => {
                        Mental.reelArea.getReel(i).layerSet.layers[0].addChildAt(newSym._symbolLayer, symPos);
                        Mental.reelArea.getReel(i).removeSymbol(symbolData, ReelModelState.STOPPED);

                        if (size > 1) {
                            while (size > symName.length) { // this should only run for enhancer
                                symName.push(symName[0]);
                                multiplierArr.push(multiplierArr[0]);
                            }

                            newSym.splitInMultiSymbols(size, symName, multiplierArr);
                            newSym.allowedToResizeSegments = true;
                            newSym.setStateAsPerParts(size);
                        } else {
                            newSym._symbolMultiplierValue = multiplierArr[0];
                            if (symPos === 2 && (i === 1 || i === 3)) {
                                newSym.setState(MentalSymbolStates.NINETY_NINE, 99);
                            } else {
                                newSym.setState(MentalSymbolStates.NONE);
                            }
                        }
                        // showing multipliers after S2's Dead reveal + anything else on the same S2.
                        this.showMultipliersOnSymbol(newSym, i, j);

                        // hiding the multipliers, which are not already visible so that, they can be visible once the blast animation is completed.
                        this.hideMultipliersOnDeadSymbolForNow(newSym, i, j);

                        this.resizeReelsAfterDeadReveal();
                    }, 0.01);

                    tl.add(() => {
                        // #167, #215
                        this.showMultipliersOnSymbol(newSym, i, j);
                    }, (tl.totalDuration() > 1) ? 0.7 : 0.01); // timing fix for dead multiplier reveal
                }
            }
        }

        // #148
        this.handleSpecialDeadMultiplier(tl, otherSymbols);

        return tl.play(0);
    }

    public removeAllDeadAnims(): void {
        for (let reel: number = 0; reel < this._deadToActiveItems.length; reel++) {
            for (let i: number = 0; i < this._deadToActiveItems[reel].length; i++) {
                this._deadToActiveItems[reel][i].stopItems();
            }
        }
        this._deadToActiveUsedCount = [0, 0, 0, 0, 0];

        this.removeChildren();
    }

    public getDeadCount(): number {
        return this._deadSymCount;
    }

    public removeAllVisibleDeadToActiveAnimations(): void {
        let currContainer: DeadToActive;
        for (let reel: number = 0; reel < this._deadToActiveItems.length; reel++) {
            for (let i: number = 0; i < this._deadToActiveItems[reel].length; i++) {
                currContainer = this._deadToActiveItems[reel][i];
                if (currContainer.name && (currContainer.name.indexOf("deadToActive") > -1 || currContainer.name.indexOf("garbageForNow") > -1)) {
                    currContainer.stopItems();
                    currContainer.name = "garbageForNow"; // we don't need it after this, and it is already removed at spin start.
                }
            }
        }
        this._deadToActiveUsedCount = [0, 0, 0, 0, 0];
    }

    public tryDeadToGoldOnNormalReels(reelId: number, isFromSplit: boolean = false, fromReelStop: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite({paused: true}),
            reelsToCheck: number[] = [0, 1, 2, 3, 4],
            gameData: IMentalGameData = Mental.model.gameData,
            symId: number = gameData.evaluatedArea[reelId].indexOf("D");

        // to fix #383 3rd point
        if (symId > -1 && (!fromReelStop || (!this.isOnEnhancer(reelId, symId) && !this.isS2Transform(reelId, symId)))) {
            this.updateDeadCounter();
            if (gameData.fireFrames[reelId][symId] || gameData.stickyFires[reelId][symId]) { // #167 point 5 fixed
                this.updateDeadCounter();
            }
        }
        // to fix #383 3rd point
        if (this.getDeadCount() > 1 && symId > -1 && (!fromReelStop || (!this.isOnEnhancer(reelId, symId) && !this.isS2Transform(reelId, symId)))) {
            // time should be added only once, since they all turns to active at same time.
            tl.add(this.turnDeadToActive(reelId, symId, isFromSplit, fromReelStop), 0);

            tl.add(() => {
                // turning to gold for previous normal reels
                for (let i = 0; i < reelsToCheck.length; i++) {
                    let activeReelId: number = reelsToCheck[i];
                    if (activeReelId <= reelId) {
                        this.turnDeadToActive(activeReelId, gameData.evaluatedArea[activeReelId].indexOf("D"), isFromSplit, fromReelStop);
                    }
                }
            }, 0);
        }

        return tl.play(0);
    }

    public tryDeadToGoldOnFeatureReel(reelId: number, symId: number, isFromSplit: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            reelsToCheck: number[] = [0, 1, 2, 3, 4],
            gameData: IMentalGameData = Mental.model.gameData;

        if (reelId === 3 && !reelsToCheck.includes(1)) { // # to fix #286
            reelsToCheck.splice(1, 0, 1);
        }

        let isXWaysReel: boolean = false;
        if (reelId == 1 || reelId == 3) {
            isXWaysReel = reelId === 1 ? this._gameData.xWaysPlayed[0] :
                this._gameData.xWaysPlayed[1];
        }

        this.updateDeadCounter(); //cz we are sure dead is there

        if (this.getDeadCount() > 1) {
            tl.add(this.turnDeadToActive(reelId, symId, isFromSplit), 0);

            //turning to gold for previous normal reels
            for (let i: number = 0; i < reelsToCheck.length; i++) {
                let activeReelId: number = reelsToCheck[i];

                if ((gameData.evaluatedArea[activeReelId].indexOf("D") > -1) || (activeReelId == 1 && gameData.firstS2SymbolName.indexOf("D") > -1)) {
                    // to fix #286
                    const symId: number = (activeReelId === 1) ? gameData.scatter2Position[1] :
                        gameData.evaluatedArea[activeReelId].indexOf("D");

                    tl.add(this.turnDeadToActive(activeReelId, symId, isFromSplit), 0);
                }
            }
        }
        if (isXWaysReel) {
            tl.add(() => FeaturePresentationHelper.resizeSymbolWithInReel(reelId, false), "+=0.01");
        }
        return tl;
    }

    public tryDeadToGoldOnEnhancerCell(reelId: number, isFromSplit: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            reelsToCheck: number[] = [0, 1, 2, 3, 4],
            gameData: IMentalGameData = Mental.model.gameData,
            symId: number = gameData.evaluatedArea[reelId].indexOf("D");

        if (symId === 2) {
            this.updateDeadCounter();
        }

        if (this.getDeadCount() > 1 && symId === 2) {
            if (this._activeIndex[reelId]) {
                this._activeIndex[reelId] = false;
            }
            tl.add(this.turnDeadToActive(reelId, symId, isFromSplit), 0);

            //turning to gold for previous normal reels
            for (let i: number = 0; i < reelsToCheck.length; i++) {
                let deadSymPos: number[] = this.getFinalDeadPositions(i);
                const totalDeadOnTheReel: number = deadSymPos.reduce((a: number, b: number): number => {
                    return (a + b);
                }, 0);

                // only turn to active if there are dead symbols on the reel
                if (totalDeadOnTheReel) {
                    for (let j: number = 0; j < deadSymPos.length; j++) {
                        if (deadSymPos[j] > 0 && !(i === reelId && j === symId)) {
                            if (i === reelId) {
                                this._activeIndex[i] = false;
                            }

                            // turn to active only if it is allowed as per the count
                            if (totalDeadOnTheReel < this._deadToActiveUsedCount[reelId]) {
                                tl.add(this.turnDeadToActive(i, j, isFromSplit), 0);
                            }
                        }
                    }
                }
            }
        }

        return tl;
    }

    public getFinalDeadPositions(reelId: number, fromReelStop: boolean = false): number[] {
        const finalDeadSymbolPos: number[][] = [[], [], [], [], []],
            gameData: IMentalGameData = Mental.model.gameData,
            deadSymData: IDeadFeature[][][] = gameData.deadSymbols;

        for (let i = 0; i < deadSymData.length; i++) {
            for (let j = 0; j < deadSymData[i].length; j++) {
                if (fromReelStop) { // to fix #383
                    if (j === gameData.scatter2Position[i] || ((i === 1 || i === 3) && j === 2)) {
                        // do not count for S2 and Enhancer when called from reel-stop
                        continue;
                    }
                }
                finalDeadSymbolPos[i][j] = deadSymData[i][j] ? deadSymData[i][j].length : 0;
            }
        }

        return finalDeadSymbolPos[reelId];
    }

    public turnDeadToActive(reelId: number, symId: number, isFromSplit: boolean, fromReelStop: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._activeIndex[reelId]) {
            return tl;
        } else {
            const finalDeadPos: number[] = this.getFinalDeadPositions(reelId, fromReelStop);

            if (finalDeadPos[symId]) {
                tl.add(this.playActiveAnim(reelId, symId, isFromSplit));

                // this should be kept below as checking for burn anim in DeadToActive.ts
                // this should always set to true when Dead fire is played on SymId of that reel
                this._activeIndex[reelId] = true;
            }

            return tl;
        }
    }

    public isS2Transform(reelId: number, symId: number): boolean {
        return (Mental.model.gameData.scatter2Position[reelId] === symId);
    }

    public isOnEnhancer(reelId: number, symId: number): boolean {
        return (symId === 2 && (reelId === 1 || reelId === 3));
    }

    public playActiveAnim(reelId: number, symId: number, isFromSplit: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            delays: number = 0,
            gameData: IMentalGameData = Mental.model.gameData;

        let symbolParts: string[] = [],
            symSize: number = gameData.currentSymbolSize[reelId][symId];

        if (this.isS2Transform(reelId, symId)) {
            symbolParts = reelId === 1 ? gameData.firstS2SymbolName : gameData.secondS2SymbolName;
            symbolParts = symbolParts.map((item: string) => {
                if (item.length === 1) { // if get DG not to make it DGG
                    return item.replace("D", "DG");
                } else {
                    return item;
                }
            });
        }

        if (symbolParts.length !== symSize) {
            symbolParts = [];
            for (let i: number = 0; i < symSize; i++) {
                symbolParts[i] = "DG";
            }
        }

        if (isFromSplit) {
            this.removeSelectedDeadAnims(reelId);
        }

        for (let i: number = 0; i < symSize; i++) {
            if (symbolParts[i] === "DG") {
                const params: IDeadToActiveParams = {
                    "reelId": reelId,
                    "symId": symId,
                    "size": symSize,
                    "segment": i
                };
                let index: number = this._deadToActiveUsedCount[reelId],
                    deadSymActiveAnim: DeadToActive = this._deadToActiveItems[reelId][index];

                deadSymActiveAnim.setParams(params);
                deadSymActiveAnim.name = "deadToActive_" + reelId + "_" + symId + "_" + i;
                this._deadToActiveUsedCount[reelId]++;

                tl.add(deadSymActiveAnim.playAnimSeq(), 0);
            }
        }

        tl.add(this.performSymbolOperation(reelId, symId, symSize, symbolParts), delays);

        return tl;
    }

    public resetActiveIndex(): void {
        this._activeIndex = [false, false, false, false, false];
        this._deadSymCount = 0;
    }

    public isCoolOrSuperFs(currMode: string): boolean {
        return (currMode === MentalGameMode.COOL_FREESPIN || currMode === MentalGameMode.SUPER_FREESPIN);
    }

    // #299
    public adjustActiveAnimOnResize(reelId: number, symId: number, innerSymId: number, height: number, absoluteIdx: number): void {
        // #333
        const item: DeadToActive = (NolimitApplication.getLayerByName(Mental.LAYERS.DEAD_TO_ACTIVE)!.getChildByName("deadToActive_" + reelId + "_" + symId + "_" + innerSymId) as DeadToActive);
        if (item) {
            item.adjustActiveAnimOnResize(height, absoluteIdx);
        }
    }

    private resizeReelsAfterDeadReveal(): void {
        const xWaysPossibleReels: number[] = Mental.model.reelEnhancerData.xWaysPossibleReels;
        for (let k: number = 0; k < xWaysPossibleReels.length; k++) {
            const enhancerXWayIndex: number = k == 0 ? 2 : 3; // last 2 indexes for enhancer xways
            const s2XWayIndex: number = k == 0 ? 0 : 1; // // first 2 indexes for s2 xways
            if (this._gameData.xWaysPlayed[enhancerXWayIndex] || this._gameData.xWaysPlayed[s2XWayIndex]) {
                FeaturePresentationHelper.resizeSymbolWithInReel(xWaysPossibleReels[k], this._gameData.xWaysPlayed[enhancerXWayIndex]);
            }
        }
    }

    private showMultipliersOnSymbol(newSym: MentalNormalSymbol, reelId: number, symId: number): void {
        if (newSym._splitSymbols.length) {
            for (let k: number = 0; k < newSym._splitSymbols.length; k++) {
                if (this._gameData.deadMultiplierPlacedPositions[reelId][symId][k]) { // to fix #352
                    newSym._splitSymbols[k]._symbolMultiplierValue = this._gameData.deadMultiplierPlacedValue[reelId][symId][k];
                }
                newSym._splitSymbols[k].showSymbolMultiplier();
            }
        } else {
            if (this._gameData.deadMultiplierPlacedPositions[reelId][symId][0]) { // to fix #352
                newSym._symbolMultiplierValue = this._gameData.deadMultiplierPlacedValue[reelId][symId][0];
            }
            newSym.showSymbolMultiplier();
        }
    }

    private hideMultipliersOnDeadSymbolForNow(newSym: MentalNormalSymbol, reelId: number, symId: number): void {
        if (newSym._splitSymbols.length) {
            for (let innerSymId: number = 0; innerSymId < newSym._splitSymbols.length; innerSymId++) {
                if (!this._gameData.deadMultiplierPlacedPositions[reelId][symId][innerSymId]) {
                    newSym._splitSymbols[innerSymId]._symbolMultiplier.visible = false;
                }
            }
        } else {
            if (!this._gameData.deadMultiplierPlacedPositions[reelId][symId][0]) {
                newSym._symbolMultiplier.visible = false;
            }
        }
    }

    private removeSelectedDeadAnims(reelId: number): void {
        let currContainer: DeadToActive;
        for (let i: number = 0; i < this._deadToActiveItems[reelId].length; i++) {
            currContainer = this._deadToActiveItems[reelId][i];
            if (currContainer.name && currContainer.name.indexOf("deadToActive_" + reelId) > -1) {
                currContainer.stopItems();
                // #299
                currContainer.name = "garbageForNow"; // we don't need it after this, and it is already removed at spin start.
            }
        }
        this._deadToActiveUsedCount[reelId] = 0;
    }

    private updateDeadCounter(): void {
        this._deadSymCount++;
    }

    private performSymbolOperation(reelId: number, symId: number, symSize: number, symbolParts: string[]): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            if (this.isOnEnhancer(reelId, symId) && symSize < 3) {
                symSize *= 2;
            }
            if (symbolParts.length !== symSize) {
                symbolParts = [];
                for (let i: number = 0; i < symSize; i++) {
                    symbolParts[i] = "DG";
                }
            }
        }, 0);
        tl.add(() => {
            const reel: MentalReel = Mental.reelArea.getReel(reelId);
            const newSym: MentalNormalSymbol = <MentalNormalSymbol>reel.model.getStopSymbolAt(symId).positionable;

            if (symbolParts.includes("S2")) {
                const splitSymbols: MentalNormalSymbol[] = newSym._splitSymbols;
                if (symbolParts.length === splitSymbols.length) {
                    for (let i: number = 0; i < symbolParts.length; i++) {
                        if (symbolParts[i] === "S2") {
                            symbolParts[i] = splitSymbols[i].symbolName;
                        }
                    }
                }
            }

            if (symbolParts.length > 1) {
                newSym.splitInMultiSymbols(symSize, symbolParts, Mental.model.gameData.deadMultiplierPlacedValue[reelId][symId]);
            } else {
                newSym.split(symSize, symbolParts[0], symSize, Mental.model.gameData.deadMultiplierPlacedValue[reelId][symId]);
            }
            newSym.allowedToResizeSegments = true;
            newSym.setStateAsPerParts(symSize);

            // added this code to avoid empty space on reels when dead feature happens.
            this._gameData = Mental.model.gameData;
            const xWaysPossibleReels: number[] = Mental.model.reelEnhancerData.xWaysPossibleReels;
            const index: number = xWaysPossibleReels.indexOf(reelId)
            if (index > -1) {
                // to fix #338 -> enhancer open after all S2 action played, so no need to check for Enhancer xWays
                const s2XWayIndex: number = index == 0 ? 0 : 1; // // first 2 indexes for s2 xways
                if (this._gameData.xWaysPlayed[s2XWayIndex]) {
                    FeaturePresentationHelper.resizeSymbolWithInReel(xWaysPossibleReels[index], this._gameData.xWaysPlayed[index + 2]);
                }
            }
        }, 0.01);

        return tl;
    }

    private handleSpecialDeadMultiplier(tl: TimelineLite, otherSymbols: boolean[][][]): void {
        let position: number = (tl.totalDuration() > 1) ? 0.7 : 0.01;
        let specialOccurred: boolean = false;
        // #369
        const previousTint: number[][][] = [],
            soundName: string = (Mental.specialDeadMultiplierRevealWin ? MentalSoundConfig.MULT_LAND : MentalSoundConfig.MULT_DEAD);

        if (Mental.specialDeadMultiplierReveal === 9999 && Mental.specialDeadMultiplierRevealWin) {
            specialOccurred = true;
            position += 2;
        } else if ((Mental.specialDeadMultiplierReveal === 9999 && !Mental.specialDeadMultiplierRevealWin) || (Mental.specialDeadMultiplierReveal === 1000 && Mental.specialDeadMultiplierRevealWin)) {
            specialOccurred = true;
            position += 1;
        } else if (Mental.specialDeadMultiplierReveal === 1000 && !Mental.specialDeadMultiplierRevealWin) {
            specialOccurred = true;
            position += 0.5;
        }

        if (specialOccurred) {
            tl.add(() => {
                const state: MentalSymbolStates = (Mental.specialDeadMultiplierReveal === 9999 ? MentalSymbolStates.SUPER_FS_ANTICIPATION : MentalSymbolStates.COOL_FS_ANTICIPATION);
                Mental.background.playWinFlickerAnim(WinRatioType.LARGE, -1);

                // #374
                SoundManager.playSoundEffect(soundName);

                for (let i: number = 0; i < otherSymbols.length; i++) {
                    previousTint[i] = [];
                    for (let j: number = 0; j < otherSymbols[i].length; j++) {
                        previousTint[i][j] = [];
                        for (let k: number = 0; k < otherSymbols[i][j].length; k++) {
                            if (otherSymbols[i][j][k]) {
                                continue;
                            }

                            let otherSym: MentalNormalSymbol = <MentalNormalSymbol>Mental.reelArea.getReel(i).model.getStopSymbolAt(j).positionable;
                            if (otherSym._splitSymbols.length) {
                                previousTint[i][j][k] = otherSym._splitSymbols[k].getTint();
                                otherSym._splitSymbols[k].setState(state);
                            } else {
                                previousTint[i][j][k] = otherSym.getTint();
                                otherSym.setState(state);
                            }
                        }
                    }
                }

                Mental.xNudgePresentation.toggleTintForSpecialMultipliers((state === MentalSymbolStates.SUPER_FS_ANTICIPATION ? 0x222222 : 0x444444));
            }, (tl.totalDuration() > 1) ? 0.71 : 0.02);
            tl.add(() => {
                Mental.background.stopWinFlicker();

                for (let i: number = 0; i < otherSymbols.length; i++) {
                    for (let j: number = 0; j < otherSymbols[i].length; j++) {
                        let otherSym: MentalNormalSymbol = <MentalNormalSymbol>Mental.reelArea.getReel(i).model.getStopSymbolAt(j).positionable;
                        for (let k: number = 0; k < otherSymbols[i][j].length; k++) {
                            if (otherSymbols[i][j][k]) {
                                continue;
                            }

                            if (otherSym._splitSymbols.length) {
                                otherSym._splitSymbols[k].applyTint(previousTint[i][j][k]);
                            } else {
                                otherSym.applyTint(previousTint[i][j][k]);
                            }
                        }
                    }
                }

                Mental.xNudgePresentation.toggleTintForSpecialMultipliers(0xffffff);
            }, position);
        }
        tl.add(() => {
            this.resizeReelsAfterDeadReveal();
        }, position);
    }
}